import React, { useCallback } from 'react';
import { useDrop } from 'react-dnd';
import DraggableCanvasComponent from './DraggableCanvasComponent';
import ComponentRenderer from './ComponentRenderer';

function DropCanvas({ 
  components, 
  onMoveComponent, 
  onSelectComponent, 
  onRemoveComponent,
  selectedComponent,
  previewMode 
}) {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ['COMPONENT', 'CANVAS_COMPONENT'],
    drop: (item, monitor) => {
      const didDrop = monitor.didDrop();
      if (didDrop) {
        return;
      }

      // Handle dropping new component from palette
      if (item.type && !item.id) {
        const newComponent = {
          id: Date.now().toString(),
          type: item.type,
          position: components.length,
          props: getDefaultProps(item.type),
          style: getDefaultStyle(item.type)
        };
        // This would need to be handled by parent component
        console.log('New component to add:', newComponent);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop()
    })
  });

  const moveComponent = useCallback((dragIndex, hoverIndex) => {
    onMoveComponent(dragIndex, hoverIndex);
  }, [onMoveComponent]);

  const isDropHighlight = isOver && canDrop;

  return (
    <div 
      ref={drop}
      className={`drop-canvas ${isDropHighlight ? 'drop-highlight' : ''} ${previewMode ? 'preview' : ''}`}
    >
      <div className="canvas-content">
        {previewMode ? (
          // Preview Mode - Render components without drag/drop
          <div className="preview-container">
            {components.map((component) => (
              <div key={component.id} className="preview-component">
                <ComponentRenderer 
                  component={component}
                  previewMode={true}
                />
              </div>
            ))}
          </div>
        ) : (
          // Edit Mode - Draggable components
          <div className="edit-container">
            {components.length === 0 ? (
              <div className="empty-canvas">
                <div className="empty-canvas-content">
                  <i className="fas fa-mouse-pointer fa-3x text-muted mb-3"></i>
                  <h4 className="text-muted">Start Building Your Page</h4>
                  <p className="text-muted">
                    Drag components from the left sidebar to begin creating your page
                  </p>
                </div>
              </div>
            ) : (
              components.map((component, index) => (
                <DraggableCanvasComponent
                  key={component.id}
                  component={component}
                  index={index}
                  moveComponent={moveComponent}
                  onSelect={onSelectComponent}
                  onRemove={onRemoveComponent}
                  isSelected={selectedComponent?.id === component.id}
                />
              ))
            )}
          </div>
        )}

        {/* Drop Zone Indicator */}
        {isDropHighlight && (
          <div className="drop-indicator">
            <i className="fas fa-plus-circle"></i>
            <span>Drop component here</span>
          </div>
        )}
      </div>

      {/* Canvas Tools */}
      {!previewMode && (
        <div className="canvas-tools">
          <div className="tool-group">
            <button 
              className="tool-btn"
              title="Undo"
              disabled
            >
              <i className="fas fa-undo"></i>
            </button>
            <button 
              className="tool-btn"
              title="Redo"
              disabled
            >
              <i className="fas fa-redo"></i>
            </button>
          </div>
          
          <div className="tool-group">
            <button 
              className="tool-btn"
              title="Clear All"
              onClick={() => {
                if (window.confirm('Are you sure you want to clear all components?')) {
                  // This would need to be handled by parent
                }
              }}
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>

          <div className="tool-group">
            <span className="tool-info">
              Components: {components.length}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper functions (would be imported from PageBuilder.js in real implementation)
function getDefaultProps(type) {
  const defaults = {
    text: { content: 'Your text here...', tag: 'p' },
    image: { src: '/placeholder.jpg', alt: 'Image' },
    button: { text: 'Click Me', link: '#' },
    banner: { title: 'Welcome', subtitle: 'Experience great food' },
    menu_section: { title: 'Our Menu' },
    contact_form: { title: 'Contact Us' },
    two_column: { leftContent: 'Left', rightContent: 'Right' },
    three_column: { leftContent: 'Left', centerContent: 'Center', rightContent: 'Right' }
  };
  return defaults[type] || {};
}

function getDefaultStyle(type) {
  const defaults = {
    text: { fontSize: '16px', color: '#333', marginBottom: '1rem' },
    image: { marginBottom: '1rem' },
    button: { padding: '12px 24px', borderRadius: '5px' },
    banner: { minHeight: '400px', padding: '4rem 0' },
    menu_section: { padding: '3rem 0', backgroundColor: '#f8f9fa' },
    contact_form: { padding: '2rem', backgroundColor: '#ffffff' },
    two_column: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' },
    three_column: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }
  };
  return defaults[type] || {};
}

export default DropCanvas;